import {
	EMAIL_LOOKUP_CREDITS,
	EmailLookupHolder,
	isEmailLookupSettled,
	isPhoneLookupSettled,
	PHONE_LOOKUP_CREDITS,
	PhoneLookupHolder,
} from "@signalist/shared-types";

import { ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
	CreditConfirmModal,
	CreditSegmentView,
	formatCredits,
} from "@/components/CreditConfirmModal";
import {
	useContactSearchForEmailsMutation,
	useContactSearchForPhonesMutation,
} from "@/pages/Private/redux/contact/contact.api";

type ContactLookup = "email" | "phone";

const UNIT_CREDITS: Record<ContactLookup, number> = {
	email: EMAIL_LOOKUP_CREDITS,
	phone: PHONE_LOOKUP_CREDITS,
};

/** Whether the lookup leaves a contact alone: the rule the API runs it by (shared-types). */
const IS_SETTLED: Record<ContactLookup, (row: ContactLookupRow) => boolean> = {
	email: isEmailLookupSettled,
	phone: isPhoneLookupSettled,
};

/** A contact of the page as the lookups' rule reads it. */
export type ContactLookupRow = { id: number } & EmailLookupHolder & PhoneLookupHolder;

/**
 * The contacts a lookup searches and the ones it skips, by the API's own rule (review S2). A
 * selected id whose row is not on the page is counted as searched, so the total stays a maximum.
 */
export function contactLookupSplit(
	lookup: ContactLookup,
	selectedItems: number[],
	rows: ContactLookupRow[]
): { searched: number; skipped: number } {
	const byId = new Map(rows.map((row) => [row.id, row]));
	const skipped = selectedItems.filter((id) => {
		const row = byId.get(id);

		return !!row && IS_SETTLED[lookup](row);
	}).length;

	return { searched: selectedItems.length - skipped, skipped };
}

const STARTED: Record<ContactLookup, string> = {
	email: "contacts.enrichEmailStarted",
	phone: "contacts.enrichPhoneStarted",
};

interface UseContactLookupsParams {
	/** The contacts the lookups send: the selected rows of the page. */
	selectedItems: number[];
	/** The page's contacts, read for which of the selected ones a lookup skips. */
	rows: ContactLookupRow[] | undefined;
	notify: (message: string) => void;
}

/**
 * Contacts' Find emails and Find phone numbers behind the credit-confirm modal (PREC-4263): the
 * dialog names the contacts the lookup searches and the ones it skips, and its maximum cost at the
 * price the API charges per result, and runs the lookup from there; a refusal stays in the dialog.
 * The API decides who is searched (`requestContactLookups`, by `isEmailLookupSettled` /
 * `isPhoneLookupSettled`), and Contacts sends only the page's selected rows, so the dialog counts
 * those rows with the same shared rule rather than asking the server. Extracted from
 * ContactsTable, which wires its action bar to `open` and renders `modal`.
 */
export function useContactLookups({ selectedItems, rows, notify }: UseContactLookupsParams) {
	const { t } = useTranslation();
	const [lookup, setLookup] = useState<ContactLookup | null>(null);
	const [searchForEmails, { isLoading: isEnrichEmailLoading }] =
		useContactSearchForEmailsMutation();
	const [searchForPhones, { isLoading: isEnrichPhoneLoading }] =
		useContactSearchForPhonesMutation();

	const run = async (action: ContactLookup) => {
		const search = action === "email" ? searchForEmails : searchForPhones;

		await search({ ids: selectedItems }).unwrap();
		notify(t(STARTED[action]));
		setLookup(null);
	};

	const count = selectedItems.length;
	const unit = lookup ? UNIT_CREDITS[lookup] : 0;
	const { searched, skipped } = lookup
		? contactLookupSplit(lookup, selectedItems, rows ?? [])
		: { searched: 0, skipped: 0 };
	const chargeNote =
		lookup === "phone"
			? t("creditConfirm.phone.chargeNote", { unit: formatCredits(PHONE_LOOKUP_CREDITS) })
			: t("creditConfirm.email.chargeNote", { count: searched });
	const segments = (action: ContactLookup): CreditSegmentView[] => [
		{
			id: "LOOKUP",
			label: t(`creditConfirm.${action}.LOOKUP`, { count: searched, unit: formatCredits(unit) }),
			count: searched,
			unitCredits: unit,
			included: true,
			resultDependent: true,
		},
		{
			id: "SKIPPED",
			label: t(`creditConfirm.${action}.SETTLED_SKIPPED`, { count: skipped }),
			count: skipped,
			unitCredits: 0,
			included: false,
		},
	];

	const modal: ReactNode = lookup && (
		<CreditConfirmModal
			isOpened
			body={
				<Trans
					components={{ b: <b className="font-semibold" /> }}
					i18nKey={`creditConfirm.${lookup}.body`}
					values={{ target: t("creditConfirm.selectedLeads", { count }) }}
				/>
			}
			chargeNote={chargeNote}
			confirmLabel={t(`creditConfirm.${lookup}.confirm`, { count: searched })}
			segments={segments(lookup)}
			title={t(`creditConfirm.${lookup}.title`, { count })}
			onClose={() => setLookup(null)}
			onConfirm={() => run(lookup)}
		/>
	);

	return {
		openEmail: () => setLookup("email"),
		openPhone: () => setLookup("phone"),
		isEnrichEmailLoading,
		isEnrichPhoneLoading,
		modal,
	};
}
